import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000'

export default function EnterpriseInviteAccept() {
  const { token } = useParams()
  const navigate = useNavigate()

  const [validating, setValidating] = useState(true)
  const [inviteValid, setInviteValid] = useState(false)
  const [orgName, setOrgName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [error, setError] = useState(null)

  useEffect(() => {
    const existingToken = localStorage.getItem('therapy_auth_token')
    if (existingToken) {
      navigate('/', { replace: true })
      return
    }

    if (!token) {
      setError('Ogiltig länk – ingen inbjudningstoken hittades.')
      setValidating(false)
      return
    }

    const validate = async () => {
      try {
        const res = await fetch(`${API_BASE}/enterprise/validate-invite/${token}`)
        const data = await res.json()
        if (data.valid) {
          setInviteValid(true)
          setOrgName(data.organization_name || '')
          setInviteEmail(data.invite_email || '')
        } else {
          setError('Inbjudningslänken är ogiltig, redan använd eller har gått ut.')
        }
      } catch {
        setError('Kunde inte kontrollera inbjudan. Kontrollera din anslutning och prova igen.')
      } finally {
        setValidating(false)
      }
    }

    validate()
  }, [token, navigate])

  /** Store invite token in sessionStorage so App.js handleLogin can link it
   *  regardless of which auth flow (signup/login/Google) the user chooses. */
  const goToAuth = (path) => {
    sessionStorage.setItem('enterprise_invite_token', token)
    navigate(path, {
      state: {
        message: `Logga in för att acceptera inbjudan från ${orgName || 'ditt företag'}.`,
        email: inviteEmail,
      },
    })
  }

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Kontrollerar inbjudning…</p>
      </div>
    )
  }

  if (!inviteValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600 text-xl font-bold">✕</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Inbjudan ej giltig</h1>
          <p className="text-gray-500 text-sm mb-6">{error}</p>
          <button onClick={() => navigate('/login')} className="text-sm text-blue-600 hover:underline">
            Gå till inloggning
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-md p-8 max-w-md w-full text-center">

        {/* Header */}
        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl">
          {orgName.charAt(0).toUpperCase() || '?'}
        </div>
        <p className="text-sm font-medium text-blue-600 uppercase tracking-wide mb-1">{orgName}</p>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Accepterar du inbjudan?</h1>
        <p className="text-gray-500 text-sm mb-8">
          {orgName} bjuder in dig att använda YourTherapist som en förmån.
          Din arbetsgivare ser aldrig vad du skriver – allt är konfidentiellt.
        </p>

        {/* Action buttons */}
        <div className="space-y-3">
          <button
            onClick={() => goToAuth('/express-signup')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-colors text-sm"
          >
            Skapa konto
          </button>
          <button
            onClick={() => goToAuth('/login')}
            className="w-full border border-gray-300 text-gray-700 font-medium py-3 rounded-xl text-sm hover:bg-gray-50 transition-colors"
          >
            Logga in med befintligt konto
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-6">
          Har du frågor? Kontakta din arbetsgivare.
        </p>
      </div>
    </div>
  )
}

